/**
 * Sub-components and utilities extracted from NoteCard.tsx.
 */
/* eslint-disable react-refresh/only-export-components -- intentional: this file mixes components and utilities */

import { ChevronDown, Lock01 } from '@untitledui/icons';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Link, useNavigate } from 'react-router-dom';

import { usePermissions } from '@/hooks/usePermissions';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import type {
  Pipeline,
  PipelineStageSchema,
} from '@/lib/crm/hooks/usePipelines';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { getAvatarInitials } from '@/lib/crm/utils/avatar';
import { toKebabSlug } from '@/lib/crm/utils/entityTypeUtils';
import { Dot } from '@/lib/foundations/dot-icon';
import { isEntityType, normalizeEntityType } from '@/lib/search';
import { formatTime, type TimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

import type { MemoEntityType } from './notes.utils';
import type { Note } from './useMemoRecords';

export const MAX_CHARS = 150;
export const MAX_LINES = 3;

// Shared pill chrome for the stage badge — used by the read-only span/Link
// variants in NoteStageLinkResolved AND the interactive picker trigger in
// NoteStagePicker, so all three render identically.
const NOTE_STAGE_PILL_CLASS =
  'inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border-secondary)] bg-primary px-2 py-0.5 text-[11px] font-medium text-secondary';

interface NoteAttributionProps {
  /**
   * Who the line is attributed to. Notes: the creator (a note is authored, not
   * assigned). Tasks: omitted — the assignee lives in the footer chip, so the
   * line carries only the time.
   */
  name?: string;
  compactTime: string;
  absoluteTime: string;
  wasEdited: boolean;
  /**
   * When true, renders an inline lock icon ahead of the attribution text, so
   * privacy reads as a property of the record (alongside who and when) rather
   * than a separate floating glyph competing for attention. The record
   * slideout passes false: there privacy is a live control at the end of this
   * row, not a static cue.
   */
  isPrivate?: boolean;
  /**
   * When provided, renders a small leading owner Avatar so this line reads as
   * the single, consolidated attribution (record slideout). Omitted on every
   * other surface, where the text-only line is unchanged.
   */
  avatarInitials?: string;
}

export function NoteAttribution({
  name,
  compactTime,
  absoluteTime,
  wasEdited,
  isPrivate = false,
  avatarInitials,
}: NoteAttributionProps) {
  // Auto-suppress when there's nothing meaningful to show. Proposal action
  // cards mount NoteCard with a synthesized note (no stored creator / owner
  // / timestamps), and prior to this guard the footer rendered a bare
  // "Unknown" line. Hiding the row when every input is empty keeps the
  // signal-to-noise ratio right without forcing every caller to opt out
  // via `showAttribution={false}`. The privacy lock is allowed to keep
  // the row visible on its own — a private note with no other metadata
  // still needs the privacy cue surfaced.
  if (!name && !compactTime && !wasEdited && !isPrivate) {
    return null;
  }
  const inner = (
    <>
      {isPrivate && (
        <Lock01
          aria-label='Private – visible to owner and creator only'
          className='size-3 shrink-0 text-quaternary'
        />
      )}
      <span>
        {name}
        {compactTime && (
          <>
            {name && <span aria-hidden='true'> · </span>}
            {compactTime}
          </>
        )}
        {wasEdited && <span className='text-quaternary'> (edited)</span>}
      </span>
    </>
  );

  // With an avatar (record slideout), wrap in a flex row so the Avatar sits
  // beside the text — the Avatar can't live inside the <p> (block-in-inline).
  if (avatarInitials) {
    return (
      <div className='mt-2 flex items-center gap-2'>
        <Avatar size='xxs' initials={avatarInitials} />
        <p
          className='flex items-center gap-1 text-xs text-tertiary'
          title={absoluteTime || undefined}
        >
          {inner}
        </p>
      </div>
    );
  }

  return (
    <p
      className='mt-2 flex items-center gap-1 text-xs text-tertiary'
      title={absoluteTime || undefined}
    >
      {inner}
    </p>
  );
}

interface NoteAttributionDataParams {
  note: Pick<Note, 'updated_at' | 'created_at'>;
  /**
   * A note is authored, so it credits its creator (falling back to the
   * owner for legacy notes with no creator association). A task is
   * assigned, not authored: the assignee is the footer chip, so its
   * attribution line carries only the time — see `attributionName` below.
   */
  entityType: MemoEntityType;
  creatorName?: string;
  ownerName?: string;
  timeFormat: TimeFormat;
}

interface NoteAttributionData {
  wasEdited: boolean;
  compactTime: string;
  absoluteTime: string;
  attributionName: string | undefined;
  attributionInitials: string | undefined;
}

/**
 * Derives the note attribution row's display values — who the line credits,
 * the compact/absolute times, whether the note was edited after creation, and
 * the avatar initials — from the raw note + display-name inputs. Rendering
 * itself stays with `NoteAttribution` (above); this hook only computes the
 * props it's handed.
 */
export function useNoteAttributionData({
  note,
  entityType,
  creatorName,
  ownerName,
  timeFormat,
}: NoteAttributionDataParams): NoteAttributionData {
  const { wasEdited, compactTime, absoluteTime } = useMemo(() => {
    const wasEdited =
      !!note.updated_at &&
      !!note.created_at &&
      new Date(note.updated_at).getTime() -
        new Date(note.created_at).getTime() >
        1000;
    const referenceTime = wasEdited ? note.updated_at : note.created_at;
    const compactTime = formatCompactRelativeTime(referenceTime);
    const absoluteTime = referenceTime
      ? new Date(referenceTime).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }) +
        ', ' +
        formatTime(referenceTime, timeFormat, { locale: 'en-US' })
      : '';
    return { wasEdited, compactTime, absoluteTime };
  }, [note.updated_at, note.created_at, timeFormat]);

  const attributionName =
    entityType === 'ahoy_task' ? undefined : creatorName || ownerName;
  const attributionInitials = useMemo(
    () => (attributionName ? getAvatarInitials(attributionName) : undefined),
    [attributionName]
  );

  return {
    wasEdited,
    compactTime,
    absoluteTime,
    attributionName,
    attributionInitials,
  };
}

interface NoteStageLinkProps {
  orgId: number;
  stageEntityId: string | null | undefined;
  /**
   * Entity type whose pipelines the stage is looked up in (and whose
   * permission gates the "open in pipeline" link) — `'ahoy_note'` for note
   * cards, `'ahoy_task'` for the task record overview's Status field.
   */
  entityType: string;
  /** Note entity_id — required (with `onStageChange`) to render the interactive picker. */
  noteEntityId?: string;
  /** When provided (with `noteEntityId`), the pill becomes an interactive stage picker. */
  onStageChange?: (
    noteEntityId: string,
    newStageEntityId: string | null
  ) => Promise<void>;
}

/**
 * Compact pill rendered in the NoteCard's footer when the note has a
 * pipeline-stage association. Reads the stage's label + color from the
 * org's `entityType` pipelines (`usePipelines(orgId, entityType)`) and
 * renders a react-router `<Link>` to the pipeline view with `?stage=<id>` —
 * PipelineBoard reads that search param and scrolls the matching column
 * into view (same deep-link mechanism deal stage UI uses; see
 * PipelineBoard's `initialScrollToStageId` effect).
 *
 * When `onStageChange` (+ `noteEntityId`) is provided, the pill instead
 * renders as an interactive stage picker (see NoteStagePicker) — clicking
 * it opens a dropdown of the pipeline's stages instead of navigating.
 *
 * Outer component early-returns on no stage so `usePipelines` is only
 * ever called for notes that actually need it — tests that render
 * stage-less notes never trigger the React-Query fetch. The inner
 * resolved component is responsible for the actual lookup + render.
 */
export function NoteStageLink({
  orgId,
  stageEntityId,
  entityType,
  noteEntityId,
  onStageChange,
}: NoteStageLinkProps) {
  // Outer guard: stage-less notes don't render a badge at all. The
  // hook in NoteStageLinkResolved only mounts when there's actually
  // something to look up — keeps stage-less callers (e.g. the
  // proposal action card's synthesized notes) from triggering a
  // useless `usePipelines` fetch and from needing the mock.
  if (!stageEntityId) {
    return null;
  }
  return (
    <NoteStageLinkResolved
      orgId={orgId}
      stageEntityId={stageEntityId}
      entityType={entityType}
      noteEntityId={noteEntityId}
      onStageChange={onStageChange}
    />
  );
}

interface NoteStageLinkResolvedProps {
  orgId: number;
  stageEntityId: string;
  entityType: string;
  noteEntityId?: string;
  onStageChange?: (
    noteEntityId: string,
    newStageEntityId: string | null
  ) => Promise<void>;
}

function NoteStageLinkResolved({
  orgId,
  stageEntityId,
  entityType,
  noteEntityId,
  onStageChange,
}: NoteStageLinkResolvedProps) {
  // `usePipelines` is a React-Query single-fetch keyed on (orgId,
  // entityType), so multiple NoteCard/TaskRecordOverview instances on the
  // same screen share the same cached result. Restricting to `entityType`
  // keeps the payload small even on orgs with many deal pipelines.
  const { pipelines } = usePipelines(orgId, entityType);
  const { hasPermission } = usePermissions();
  const canOpenInPipeline = hasPermission(entityType);
  const resolved = useMemo(() => {
    for (const pipeline of pipelines) {
      const stage = pipeline.stages.find(s => s.entity_id === stageEntityId);
      if (stage && pipeline.entity_id) {
        return { stage, pipelineId: pipeline.entity_id };
      }
    }
    return null;
  }, [pipelines, stageEntityId]);

  if (!resolved) {
    return null;
  }

  const { stage, pipelineId } = resolved;

  // Interactive picker: only when both a callback and the note's id are
  // provided. Otherwise fall through to the existing read-only rendering.
  if (onStageChange && noteEntityId) {
    return (
      <NoteStagePicker
        orgId={orgId}
        entityType={entityType}
        pipelines={pipelines}
        currentStage={stage}
        currentPipelineId={pipelineId}
        savedStageEntityId={stageEntityId}
        noteEntityId={noteEntityId}
        onStageChange={onStageChange}
        canOpenInPipeline={canOpenInPipeline}
      />
    );
  }

  const color = stage.properties.color ?? null;
  const innerContent = (
    <>
      {color && (
        <span
          aria-hidden='true'
          className='inline-block size-2 rounded-full shrink-0'
          // Inline color is mandatory here — the stage's hex value is
          // dynamic, set by the user when they configure the pipeline.
          // Tailwind arbitrary-value syntax can't be templated at runtime,
          // so this is the one place a `style={{}}` prop is justified per
          // the no-inline-styles rule.
          style={{ backgroundColor: color }}
        />
      )}
      <span className='truncate max-w-[140px]'>{stage.label}</span>
    </>
  );

  if (!canOpenInPipeline) {
    return <span className={NOTE_STAGE_PILL_CLASS}>{innerContent}</span>;
  }

  // Match the per-org URL shape used by SideNavigation + PipelinePage:
  //   /<orgId>/pipeline/<entitySlug>/<pipelineId>?stage=<stageId>
  // The kebab-cased slug for `entityType` is what GenericCrmList resolves
  // back to the entity type.
  const to = `/${orgId}/pipeline/${toKebabSlug(entityType)}/${pipelineId}?stage=${stage.entity_id}`;

  return (
    <Link
      to={to}
      title={`Open ${stage.label} in the pipeline`}
      // Stop propagation so the click navigates to the pipeline rather
      // than triggering an outer card-level handler (e.g. the slideout's
      // open-record click target).
      onClick={event => event.stopPropagation()}
      className={`${NOTE_STAGE_PILL_CLASS} hover:bg-[var(--color-bg-tertiary)] hover:text-primary transition-colors`}
    >
      {innerContent}
    </Link>
  );
}

/** Pipeline group built for the picker dropdown — mirrors AssociatedPipelineStageInput's grouping. */
interface NoteStagePickerGroup {
  name: string;
  label: string;
  stages: { entity_id: string; label: string; color?: string | null }[];
}

const OPEN_IN_PIPELINE_KEY = '__open_in_pipeline__';
const CLEAR_STATUS_KEY = '__clear_status__';

interface NoteStagePickerProps {
  orgId: number;
  entityType: string;
  pipelines: Pipeline[];
  currentStage: PipelineStageSchema;
  currentPipelineId: string;
  savedStageEntityId: string;
  noteEntityId: string;
  onStageChange: (
    noteEntityId: string,
    newStageEntityId: string | null
  ) => Promise<void>;
  canOpenInPipeline: boolean;
}

/**
 * Interactive stage picker rendered in place of the read-only stage pill
 * when the caller supplies `onStageChange`. Opens a `lib/base` Dropdown
 * listing the note pipeline's stages (grouped by pipeline when there is
 * more than one), plus an "Open in pipeline" item.
 *
 * Kept as its own module-scope component (rather than inlined into
 * NoteStageLinkResolved) so its hooks — and the open dropdown itself —
 * aren't remounted by every re-render of the resolving parent; inlining
 * would also trip `react/no-unstable-nested-components`.
 */
function NoteStagePicker({
  orgId,
  entityType,
  pipelines,
  currentStage,
  currentPipelineId,
  savedStageEntityId,
  noteEntityId,
  onStageChange,
  canOpenInPipeline,
}: NoteStagePickerProps) {
  const pipelineGroups = useMemo<NoteStagePickerGroup[]>(() => {
    return [...pipelines]
      .sort((a, b) => {
        if (a.is_default && !b.is_default) {
          return -1;
        }
        if (!a.is_default && b.is_default) {
          return 1;
        }
        return 0;
      })
      .map(pipeline => ({
        name: pipeline.pipeline_name,
        label: pipeline.label,
        stages: pipeline.stages
          .filter(s => s.entity_id != null)
          .sort((a, b) => a.properties.order - b.properties.order)
          .map(s => ({
            entity_id: s.entity_id ?? '',
            label: s.label,
            color: s.properties.color,
          })),
      }))
      .filter(g => g.stages.length > 0);
  }, [pipelines]);

  const hasMultiplePipelines = pipelineGroups.length > 1;

  // Optimistic override — set while a stage-change save is in flight, so
  // the pill reflects the user's pick immediately rather than waiting on
  // the note to refetch. Cleared whenever the note's saved stage changes
  // (success path lands here once the parent's data refreshes); the
  // `.catch` on the save below also clears it directly on failure.
  const [optimisticStageId, setOptimisticStageId] = useState<string | null>(
    null
  );
  useEffect(() => {
    setOptimisticStageId(null);
  }, [savedStageEntityId]);

  const displayStageId = optimisticStageId ?? savedStageEntityId;
  const displayStage = useMemo(() => {
    for (const group of pipelineGroups) {
      const stage = group.stages.find(s => s.entity_id === displayStageId);
      if (stage) {
        return stage;
      }
    }
    return {
      entity_id: currentStage.entity_id ?? displayStageId,
      label: currentStage.label,
      color: currentStage.properties.color,
    };
  }, [pipelineGroups, displayStageId, currentStage]);

  const selectedKeys = useMemo(
    () => new Set([displayStageId]),
    [displayStageId]
  );

  const navigate = useNavigate();

  const handleAction = useCallback(
    (key: string | number) => {
      if (key === OPEN_IN_PIPELINE_KEY) {
        navigate(
          `/${orgId}/pipeline/${toKebabSlug(entityType)}/${currentPipelineId}?stage=${savedStageEntityId}`
        );
        return;
      }
      if (key === CLEAR_STATUS_KEY) {
        // Remove the stage entirely ("un-task"). The mutation emits an
        // association-removed event; once it lands the note has no stage and
        // NoteStageLink stops rendering this pill.
        void onStageChange(noteEntityId, null).catch(() => {});
        return;
      }
      if (key === displayStageId) {
        return;
      }
      setOptimisticStageId(key as string);
      void onStageChange(noteEntityId, key as string).catch(() => {
        setOptimisticStageId(null);
      });
    },
    [
      displayStageId,
      noteEntityId,
      onStageChange,
      navigate,
      orgId,
      entityType,
      currentPipelineId,
      savedStageEntityId,
    ]
  );

  const color = displayStage.color ?? null;

  return (
    <span onClick={event => event.stopPropagation()} title={displayStage.label}>
      <Dropdown.Root>
        <AriaButton
          isDisabled={optimisticStageId !== null}
          className={cx(
            NOTE_STAGE_PILL_CLASS,
            'hover:bg-[var(--color-bg-tertiary)] hover:text-primary transition-colors disabled:opacity-70'
          )}
          aria-label={`Change stage (currently ${displayStage.label})`}
        >
          {color && (
            <span
              aria-hidden='true'
              className='inline-block size-2 rounded-full shrink-0'
              // Inline color is mandatory here — the stage's hex value is
              // dynamic, set by the user when they configure the pipeline.
              // Tailwind arbitrary-value syntax can't be templated at runtime,
              // so this is the one place a `style={{}}` prop is justified per
              // the no-inline-styles rule.
              style={{ backgroundColor: color }}
            />
          )}
          <span className='truncate max-w-[140px]'>{displayStage.label}</span>
          <ChevronDown
            aria-hidden='true'
            className='size-3 shrink-0 text-fg-quaternary'
          />
        </AriaButton>
        <Dropdown.Popover
          placement='bottom end'
          className='min-w-[var(--trigger-width)] w-auto max-w-64'
        >
          <Dropdown.Menu onAction={handleAction} selectedKeys={selectedKeys}>
            {hasMultiplePipelines
              ? pipelineGroups.map(group => (
                  <Dropdown.Section key={group.name}>
                    <Dropdown.SectionHeader className='px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-fg-quaternary'>
                      {group.label}
                    </Dropdown.SectionHeader>
                    {group.stages.map(stage => (
                      <Dropdown.Item
                        key={stage.entity_id}
                        id={stage.entity_id}
                        textValue={stage.label}
                      >
                        {stage.color && (
                          <Dot
                            size='sm'
                            style={{ color: stage.color }}
                            aria-hidden='true'
                          />
                        )}
                        {stage.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Section>
                ))
              : pipelineGroups
                  .flatMap(g => g.stages)
                  .map(stage => (
                    <Dropdown.Item
                      key={stage.entity_id}
                      id={stage.entity_id}
                      textValue={stage.label}
                    >
                      {stage.color && (
                        <Dot
                          size='sm'
                          style={{ color: stage.color }}
                          aria-hidden='true'
                        />
                      )}
                      {stage.label}
                    </Dropdown.Item>
                  ))}
            {/* Clear status — removes the stage, turning the task back into a
                plain note. Always available in the picker (it only renders when
                a stage is set). */}
            <Dropdown.Separator />
            <Dropdown.Item
              key={CLEAR_STATUS_KEY}
              id={CLEAR_STATUS_KEY}
              textValue='Clear status'
            >
              Clear status
            </Dropdown.Item>
            {canOpenInPipeline && (
              <>
                <Dropdown.Separator />
                <Dropdown.Item
                  key={OPEN_IN_PIPELINE_KEY}
                  id={OPEN_IN_PIPELINE_KEY}
                  textValue='Open in pipeline'
                >
                  Open in pipeline
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>
    </span>
  );
}

interface NoteCardStageBadgeProps {
  orgId: number;
  /** Stage badge only renders for tasks — notes have no pipeline/stage. */
  entityType: MemoEntityType;
  stageEntityId: string | null | undefined;
  noteEntityId: string;
  onStageChange?: (
    noteEntityId: string,
    newStageEntityId: string | null
  ) => Promise<void>;
}

/**
 * NoteCard's stage badge slot for its right-hand column: renders the
 * interactive/read-only `NoteStageLink` pill for tasks, nothing for notes
 * (which have no pipeline-stage concept). Callers render this unconditionally
 * — the entity-type gate lives here rather than at each call site.
 */
export function NoteCardStageBadge({
  orgId,
  entityType,
  stageEntityId,
  noteEntityId,
  onStageChange,
}: NoteCardStageBadgeProps) {
  if (entityType !== 'ahoy_task') {
    return null;
  }
  return (
    <NoteStageLink
      orgId={orgId}
      entityType={entityType}
      stageEntityId={stageEntityId}
      noteEntityId={noteEntityId}
      onStageChange={onStageChange}
    />
  );
}

/**
 * Compact relative-time formatter for note subtitles. Keeps the metadata
 * line short — e.g. "5h ago" instead of "about 5 hours ago".
 */
export function formatCompactRelativeTime(
  value: string | null | undefined
): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return '';
  }
  // Floor-based conversion keeps each unit accurate up to its boundary:
  // "59m ago" stays as minutes until a full hour has elapsed, "23h ago"
  // stays as hours until a full day has elapsed. Rounding bumped values
  // up prematurely (e.g. 30 minutes → "1h ago", 12 hours → "1d ago"),
  // misrepresenting the elapsed time at unit boundaries.
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 45) {
    return 'just now';
  }
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    return `${diffHr}h ago`;
  }
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) {
    return `${diffDay}d ago`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Smart truncation for HTML content that doesn't cut in the middle of tags.
 * Returns truncated HTML that preserves complete tags and structure.
 *
 * DOMPurify is configured to keep Entity/NamedEntity/mention tags and their
 * mention-related attributes — by default DOMPurify strips unknown tags
 * (keeping inner text), which would silently delete every entity mention
 * before the truncation walk could preserve them, leaving MarkdownContent
 * with nothing to render as a badge. Legacy mention payloads also encode
 * the referenced record via `id` + `type` (rather than `entity-id`), so we
 * keep those attributes here too — without them the truncated preview
 * would lose the metadata MentionBadge needs to render. The remaining
 * sanitisation guards against scripts and event handlers in case any
 * unsafe HTML ever ends up stored.
 */
export function smartTruncateHtml(
  content: string,
  maxChars: number
): { html: string; truncated: boolean } {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = DOMPurify.sanitize(content, {
    ADD_TAGS: ['entity', 'namedentity', 'mention'],
    ADD_ATTR: ['entity-id', 'id', 'type'],
  });

  let charCount = 0;
  let truncated = false;

  function truncateNode(node: Node): Node | null {
    if (truncated) {
      return null;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (charCount + text.length > maxChars) {
        const remaining = maxChars - charCount;
        truncated = true;
        // Always signal the trim with an ellipsis — even when the budget is
        // already exhausted (remaining === 0) and this whole text node is
        // dropped — so a truncated preview never looks complete.
        return document.createTextNode(
          remaining > 0 ? text.slice(0, remaining) + '…' : '…'
        );
      }
      charCount += text.length;
      return node.cloneNode(true);
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      // For Entity mentions, count the display text but preserve the full tag
      if (
        tagName === 'entity' ||
        tagName === 'namedentity' ||
        tagName === 'mention'
      ) {
        const displayText = element.textContent || '';
        if (charCount + displayText.length > maxChars) {
          truncated = true;
          // The mention doesn't fit and we never cut a tag mid-way, so the
          // whole node is dropped. Emit an ellipsis in its place — matching
          // the text-node branch — so the preview still signals that content
          // was removed rather than looking complete.
          return document.createTextNode('…');
        }
        charCount += displayText.length;
        return element.cloneNode(true);
      }

      // For other elements, recursively process children
      const clone = element.cloneNode(false) as Element;
      for (const child of Array.from(element.childNodes)) {
        const truncatedChild = truncateNode(child);
        if (truncatedChild) {
          clone.appendChild(truncatedChild);
        }
        if (truncated) {
          break;
        }
      }
      // Only return if the element has content
      if (clone.childNodes.length > 0 || ['br'].includes(tagName)) {
        return clone;
      }
      return null;
    }

    return null;
  }

  const resultDiv = document.createElement('div');
  for (const child of Array.from(tempDiv.childNodes)) {
    const truncatedChild = truncateNode(child);
    if (truncatedChild) {
      resultDiv.appendChild(truncatedChild);
    }
    if (truncated) {
      break;
    }
  }

  return { html: resultDiv.innerHTML, truncated };
}

const TAG_PATTERN =
  /<(?:mention|NamedEntity|Entity)[^>]*>[^<]*<\/(?:mention|NamedEntity|Entity)>|<@[^>]+>/gi;

// Longest HTML character reference we guard against (e.g. `&#x1F600;`), plus
// a little slack. Bounds the look-back so the entity check stays O(1).
const MAX_HTML_ENTITY_LEN = 12;
// A fragment that looks like the START of an HTML character reference (no
// terminating `;`). Used to detect a cut that bisected an entity. Two shapes:
//   • numeric: `&#`, optional `x`/`X` for hex, then any hex/decimal digits —
//     `&#` alone already commits to a reference, so zero trailing digits is fine
//   • named:   `&` followed by AT LEAST ONE alphanumeric (`&a`, `&amp`)
// A bare `&` matches NEITHER branch, so a literal ampersand (e.g. `Tom &`) is
// left in place rather than dropped as if it were a split entity.
const PARTIAL_HTML_ENTITY = /^&(?:#[xX]?[0-9a-zA-Z]*|[0-9a-zA-Z]+)$/;

/**
 * If `cut` lands inside an unterminated HTML character reference — e.g. the
 * plain-text budget bisects `&amp;` into `&am` — back the cut up to the `&`.
 *
 * The excerpt is rendered by MarkdownContent, whose always-on raw-HTML
 * pipeline (rehypeRaw) decodes character references; a half-entity like `&am`
 * would surface as broken literal text. Backing off drops the fragment
 * cleanly (the trailing ellipsis still signals the trim) instead of showing a
 * corrupted entity. A completed reference already contains its `;` inside the
 * kept slice, so it is left untouched.
 */
function backOffMidHtmlEntity(text: string, cut: number): number {
  if (cut <= 0 || cut >= text.length) {
    return cut;
  }
  const ampIndex = text.lastIndexOf('&', cut - 1);
  if (ampIndex < 0 || cut - ampIndex > MAX_HTML_ENTITY_LEN) {
    return cut;
  }
  const fragment = text.slice(ampIndex, cut);
  // Terminated reference (`;` present) → not split → leave the cut alone.
  if (fragment.includes(';')) {
    return cut;
  }
  // Only back off when the fragment genuinely looks like the start of a
  // reference (no whitespace / stray `&`), otherwise it's a literal ampersand.
  if (!PARTIAL_HTML_ENTITY.test(fragment)) {
    return cut;
  }
  return ampIndex;
}

/**
 * Smart truncation for plain text that doesn't cut in the middle of Entity/mention tags.
 * Returns truncated content that preserves complete mention tags.
 */
export function smartTruncatePlainText(
  content: string,
  maxChars: number,
  maxLines: number
): string {
  // Split by lines first
  const lines = content.split('\n');
  const truncatedLines = lines.slice(0, maxLines);
  let result = truncatedLines.join('\n');

  // If we have more lines than allowed, mark as truncated
  const hadMoreLines = lines.length > maxLines;

  // Now handle character limit while respecting tag boundaries
  // Regex to match all mention formats (same as ALL_MENTIONS_PATTERN but simpler)

  // Build a map of tag positions and their display lengths
  const tags: { start: number; end: number; displayLength: number }[] = [];
  let match;
  TAG_PATTERN.lastIndex = 0;
  while ((match = TAG_PATTERN.exec(result)) !== null) {
    // Extract display name from the tag to calculate display length
    const displayMatch =
      match[0].match(/>([^<]*)<\//) || match[0].match(/\|([^>]+)>/);
    const displayName = displayMatch ? displayMatch[1] : '';
    // Validate entity type before using for UI logic
    // Extract entity type to determine if @ prefix is rendered
    // user types render with @ prefix, others don't
    const typeMatch = match[0].match(/type="(\w+)"/);
    const isSlackFormat = match[0].startsWith('<@'); // Slack format is always user
    let entityType: string | null;
    if (isSlackFormat) {
      entityType = 'user';
    } else if (typeMatch) {
      entityType = typeMatch[1];
    } else {
      entityType = null;
    }
    // Normalize and check against canonical type
    const normalizedType = entityType ? normalizeEntityType(entityType) : null;
    let hasAtPrefix = false;
    if (normalizedType && isEntityType(normalizedType)) {
      hasAtPrefix = normalizedType === 'ahoy_user';
    }
    tags.push({
      start: match.index,
      end: match.index + match[0].length,
      displayLength: displayName.length + (hasAtPrefix ? 1 : 0), // +1 for @ prefix only for users
    });
  }

  // Calculate display length up to each character position
  // and find where to cut
  let displayLength = 0;
  let cutPosition = result.length;

  for (let i = 0; i < result.length; i++) {
    // Check if we're entering a tag
    const tagAtPosition = tags.find(t => t.start === i);
    if (tagAtPosition) {
      // Add the display length of the entire tag
      displayLength += tagAtPosition.displayLength;
      if (displayLength > maxChars) {
        // Don't include this tag, cut before it
        cutPosition = i;
        break;
      }
      // Skip to end of tag
      i = tagAtPosition.end - 1;
      continue;
    }

    // Regular character
    displayLength++;
    if (displayLength > maxChars) {
      cutPosition = i;
      break;
    }
  }

  // If we need to truncate
  if (cutPosition < result.length || hadMoreLines) {
    // Guard against slicing through an HTML character reference (e.g. `&amp;`)
    // that MarkdownContent would later decode into broken literal text.
    const safeCut = backOffMidHtmlEntity(result, cutPosition);
    result = result.slice(0, safeCut) + '…';
  }

  return result;
}

/**
 * Check if content contains HTML tags (beyond Entity mentions).
 *
 * Used by truncation to decide between HTML-aware vs plain-text smart
 * truncation.  Display rendering itself is delegated to MarkdownContent
 * (with rehypeRaw + rehypeSanitize + the entity span renderer), so we
 * no longer maintain a parallel HTML/mention parser here.
 */
export function containsHtmlTags(content: string): boolean {
  const entityPattern =
    /<(?:Entity|NamedEntity|mention)\s+[^>]+>[^<]*<\/(?:Entity|NamedEntity|mention)>/gi;
  const withoutEntities = content.replace(entityPattern, '');
  return /<\s*\/?\s*[a-zA-Z][a-zA-Z0-9]*(?:\s[^>]*)?\s*\/?>/i.test(
    withoutEntities
  );
}

// Re-export Note type used by sub-components to avoid callers needing a second import
export type { Note };
